import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Button from '../../../../../incase-domain/components/button/Button';
import Popover from '../../../../../incase-domain/components/popover/Popover';
import Chip from '../../../../../incase-domain/components/chip/Chip';
import {BooleanFilterControl, type BooleanFilterLabelKey} from './BooleanFilterControl';
import {StringArrayFilterControl} from './StringArrayFilterControl';

export enum FilterDataType {
    BOOLEAN,
    STRING,
    STRING_ARRAY,
    NUMBER,
    DATE,
}

export type FilterData = FilterDataBoolean | FilterDataDropdown;

export interface FilterDataBoolean {
    type: FilterDataType.BOOLEAN;
    key: string;
    initialValue: boolean | undefined;
    labelTrue?: BooleanFilterLabelKey;
    labelFalse?: BooleanFilterLabelKey;
    labelBoth?: BooleanFilterLabelKey;
}

export interface FilterDataDropdown {
    type: FilterDataType.STRING_ARRAY;
    key: string;
    initialValue: string[] | undefined;
    options: string[];
}

export type FilterDefinition = FilterDefinitionBoolean | FilterDefinitionDropdown;

export type FilterDefinitionBoolean = Omit<FilterDataBoolean, 'initialValue'>;
export type FilterDefinitionDropdown = Omit<FilterDataDropdown, 'initialValue'>;

interface FilterProps {
    filters: FilterData[];
    onChange: (value: FilterData[]) => void;
}

export function Filter(props: FilterProps): JSX.Element {
    const {filters, onChange} = props;

    const {t} = useTranslation();

    const [currentFilters, setCurrentFilters] = useState<FilterData[]>(filters);

    // * Used only to keep track of filters that have non-default value (for badge indicator count)
    const [activeFilters, setActiveFilters] = useState<FilterData[]>(getActiveFilters());

    useEffect(() => {
        setCurrentFilters(filters);
    }, [filters]);

    useEffect(() => {
        setActiveFilters(getActiveFilters());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFilters]);

    function updateFilter(key: string, value: boolean | string[] | number | Date | undefined): void {
        const currentFiltersCopy: FilterData[] = currentFilters.map((f) => {
            if (f.key !== key) return f;

            switch (f.type) {
                case FilterDataType.BOOLEAN:
                    return {...f, initialValue: value as boolean | undefined};
                case FilterDataType.STRING_ARRAY:
                    return {...f, initialValue: value as string[] | undefined};
                default:
                    return f;
            }
        });

        setCurrentFilters(currentFiltersCopy);
        onChange(currentFiltersCopy);
    }

    function clearFilters(): void {
        const clearedFilters: FilterData[] = currentFilters.map((f) => {
            if (f.type === FilterDataType.BOOLEAN) {
                return {...f, initialValue: undefined};
            }
            if (f.type === FilterDataType.STRING_ARRAY) {
                return {...f, initialValue: []};
            }
            return f;
        });

        setCurrentFilters(clearedFilters);
        onChange(clearedFilters);
    }

    function getActiveFilters(): FilterData[] {
        return currentFilters.filter((f) => {
            if (f.type === FilterDataType.BOOLEAN) {
                return f.initialValue !== undefined;
            }
            if (f.type === FilterDataType.STRING_ARRAY) {
                return (f.initialValue?.length || 0) > 0;
            }
            return false;
        });
    }

    return filters.length ? (
        <>
            <Popover
                content={
                    <>
                        <div className="c-genericTable__filter_header">
                            <div className="c-genericTable__filter_title">{t('genericTable.filtersTitle')}</div>
                            {activeFilters.length > 0 && (
                                <Button
                                    size="sm"
                                    className="c-genericTable__filter_clearButton"
                                    variant="ghost"
                                    color="error"
                                    onClick={clearFilters}
                                >
                                    {t('common.reset')}
                                </Button>
                            )}
                        </div>
                        <div className="u-flex u-flex-col u-gap-4">
                            {currentFilters.map((el) => {
                                return (
                                    <div key={el.key}>
                                        <div className="c-genericTable__filter_label">{el.key}</div>
                                        {el.type === FilterDataType.BOOLEAN && (
                                            <BooleanFilterControl
                                                key={el.key}
                                                value={el.initialValue}
                                                onChange={(next) => updateFilter(el.key, next)}
                                                labelBoth={el.labelBoth}
                                                labelTrue={el.labelTrue}
                                                labelFalse={el.labelFalse}
                                            />
                                        )}
                                        {el.type === FilterDataType.STRING_ARRAY && (
                                            <StringArrayFilterControl
                                                key={el.key}
                                                value={el.initialValue}
                                                options={el.options}
                                                onChange={(next) => updateFilter(el.key, next)}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                }
            >
                <Chip content={activeFilters.length}>
                    <Button leading="solar:tuning-linear" variant="soft" size="sm" square />
                </Chip>
            </Popover>
        </>
    ) : (
        <></>
    );
}

export default Filter;
